import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import LoadingSpinner from '@/common/components/LoadingSpinner'
import Table from '@/common/components/Table'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { priceSurveyStatusOptions } from '@/common/constants/priceSurveyStatusOptions'
import { useCities } from '@/common/hooks/queries/useCities'
import useCompany from '@/common/hooks/queries/useCompany'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { Mapper } from '@/common/services/mapper'
import { ProductData } from '@/common/types/product.type'
import { companyPriceSurveyColumns } from '@/features/privateCompanies/constants/priceSurvey/companyPriceSurveyColumns.constant'
import { useCompanyPriceSurveys } from '@/features/privateCompanies/hooks/priceSurvey/useCompanyPriceSurveys'
import { CompanyPriceSurveyTransformer } from '@/features/privateCompanies/services/priceSurvey/companyPriceSurveyTransformer'
import { Link, useParams } from 'react-router-dom'

const List = () => {
  const { id } = useParams()

  const { filterParams, setFilterParams } = useFilterParams()
  const { date, formattedDate } = useFilterDate(filterParams)

  const [searchCity, setSearchCity] = useDebounce('')
  const [searchProduct, setSearchProduct] = useDebounce('')

  const { data: cityOptions } = useCities({
    filters: { search: searchCity, stateId: filterParams.state?.value },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  })

  const { data: productOptions } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: company, isPending: companyIsPending } = useCompany({
    id: id || '',
  });

  const {
    data: companyPriceSurveys,
    meta,
    isPending: companyPriceSurveysIsPending,
    isFetching: companyPriceSurveysIsFetching,
    error: companyPriceSurveysError = {} as any,
    refetch: refetchCompanyPriceSurveys,
  } = useCompanyPriceSurveys({
    filters: {
      page: filterParams.page || 1,
      companyId: id || '',
      cityId: filterParams.city?.value,
      status: filterParams.status?.value,
      productId: filterParams.product?.value,
      surveyConductedAtGt: formattedDate.from,
      surveyConductedAtLt: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: CompanyPriceSurveyTransformer.tableTransformer({
        companyId: id || '',
        operationKlassToCheckPermission: 'Admin::Client::PriceSurveys::Show',
      }),
    }),
  })

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyPriceSurveysError?.status}
      refetchFunction={refetchCompanyPriceSurveys}
    >
      <div className="flex flex-col gap-6">
        <div className="flex justify-between gap-4">
          <div className="flex gap-x-4">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  placeholder: 'Cidade',
                  options: [{ label: 'Todas', value: '' }, ...cityOptions],
                  value: filterParams.city,
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, city: e?.value ? e : null })),
                  onInputChange: setSearchCity,
                },
                {
                  className: 'min-w-40',
                  placeholder: 'Status',
                  options: [{ label: 'Todos', value: '' }, ...priceSurveyStatusOptions],
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, status: e?.value ? e : null })),
                  value: filterParams.status,
                },
                {
                  className: 'min-w-40',
                  placeholder: 'Produto',
                  options: [{ label: 'Todos', value: '' }, ...productOptions],
                  onChange: (e: any) => setFilterParams((prev) => ({ ...prev, product: e?.value ? e : null })),
                  onInputChange: setSearchProduct,
                  value: filterParams.product,
                },
              ]}
            />
          </div>

          <div className='flex items-center gap-2'>
            <LoadingSpinner isLoading={companyPriceSurveysIsFetching} />
            <Link
              to={companyPriceSurveysIsPending ? '' : `/companies/${id}/price-survey/create`}
              className={companyPriceSurveysIsPending ? 'cursor-default' : ''}
            >
              <Button
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                data-testid="create_company_priceSurvey"
                disabled={
                  companyPriceSurveysIsPending ||
                  companyIsPending ||
                  company?.price_survey_mode === 'disabled'
                }
                operationKlassToCheckPermission="Admin::Client::PriceSurveys::Create"
              >
                Criar pesquisa
              </Button>
            </Link>
          </div>

        </div>

        <Table
          className="max-h-[calc(100vh-256px)]"
          rows={companyPriceSurveys}
          columns={companyPriceSurveyColumns}
          isLoadingData={companyPriceSurveysIsPending}
          page={filterParams.page || 1}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          hasPagination
          rowEmptyValue="--"
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default List
